import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  TextField,
  Button,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Paper,
  IconButton,
  InputAdornment,
  Grid,
  useMediaQuery,
  useTheme,
  Avatar,
  Link as MuiLink,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BabyChangingStationIcon from '@mui/icons-material/BabyChangingStation';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import { useNavigate } from 'react-router-dom';
import { PlaceAutocompleteTextField, PlaceResult } from '../features/location/PlaceAutocompleteTextField';
import { getLocations, getFeaturedLocations } from '../api';

// Halifax, NS coordinates
const HALIFAX_CENTER = { lat: 44.6488, lng: -63.5752 };

// Placeholder images for featured cards
const featuredImages = {
  feeding: 'https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=300&h=200&fit=crop',
  supplies: 'https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=300&h=200&fit=crop',
  diaper: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=300&h=200&fit=crop',
};

// Location from API
interface Location {
  id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  type: string;
  hoursOfOperation?: string;
  imageUrl?: string;
}

interface FeaturedSpot {
  id: string;
  title: string;
  description: string;
  image: string;
}

const featuredSpots: FeaturedSpot[] = [
  {
    id: '1',
    title: 'Featured Feeding Spot',
    description: 'Discover a peaceful spot ideal for feeding your baby, conveniently located near you.',
    image: featuredImages.feeding,
  },
  {
    id: '2',
    title: 'Nearby Baby Supply Store',
    description: 'Find all the essential baby products at this nearby store.',
    image: featuredImages.supplies,
  },
  {
    id: '3',
    title: 'Nearby Diaper Changing Station',
    description: 'Access clean and comfortable diaper changing facilities close by.',
    image: featuredImages.diaper,
  },
];

const categories = [
  'Diapers',
  'Feeding Spots',
  'Supply Stores',
  'Play Areas',
  'Restrooms',
  'Parking',
];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [featuredLocations, setFeaturedLocations] = useState<Location[]>([]);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  
  // Use refs for address to avoid re-renders when autocomplete updates
  const locationAddressRef = useRef('');
  const selectedPlaceRef = useRef<PlaceResult | null>(null);
  const autoNavTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePlaceSelect = useCallback((place: PlaceResult) => {
    selectedPlaceRef.current = place;
    locationAddressRef.current = place.address;

    // Clear any pending auto-nav
    if (autoNavTimerRef.current) clearTimeout(autoNavTimerRef.current);

    // Auto-navigate after 2s of inactivity
    autoNavTimerRef.current = setTimeout(() => {
      if (selectedPlaceRef.current) {
        navigate('/post-location', {
          state: { place: selectedPlaceRef.current },
        });
      }
    }, 2000);
  }, [navigate]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoNavTimerRef.current) clearTimeout(autoNavTimerRef.current);
    };
  }, []);
  
  // Fetch locations from API
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const [allLocs, featuredLocs] = await Promise.all([
          getLocations(),
          getFeaturedLocations()
        ]);
        setLocations(allLocs);
        setFeaturedLocations(featuredLocs);
      } catch (err) {
        console.error('Failed to fetch locations:', err);
      }
    };
    fetchLocations();
  }, []);
  
  // Check auth status on mount and redirect if not logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    setIsLoggedIn(true);
    setAuthChecked(true);
  }, [navigate]);

  // Initialize Google Map only once when auth is checked
  useEffect(() => {
    if (!authChecked || !mapRef.current) return;
    if (mapInstanceRef.current) return; // Already initialized
    
    const initMap = () => {
      if (window.google && window.google.maps && mapRef.current && !mapInstanceRef.current) {
        console.log('Initializing Google Map...');
        mapInstanceRef.current = new google.maps.Map(mapRef.current, {
          center: HALIFAX_CENTER,
          zoom: 13,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          scaleControl: false,
          rotateControl: false,
          clickableIcons: false,
        });
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      if (window.google) {
        initMap();
      } else {
        const checkGoogle = setInterval(() => {
          if (window.google) {
            clearInterval(checkGoogle);
            initMap();
          }
        }, 100);
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [authChecked]);

  // Add markers when locations change (separate from map init)
  useEffect(() => {
    if (!mapInstanceRef.current || locations.length === 0) return;
    
    console.log('Adding markers for', locations.length, 'locations');
    
    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    
    // Add markers for each location
    locations.forEach(loc => {
      const marker = new google.maps.Marker({
        position: { lat: loc.latitude, lng: loc.longitude },
        map: mapInstanceRef.current!,
        title: loc.name,
      });

      // Add info window with clickable content
      const infoWindowContent = document.createElement('div');
      infoWindowContent.style.cssText = 'padding: 0; width: 260px; user-select: none; cursor: default; outline: none; caret-color: transparent; font-family: "Circular", "Poppins", -apple-system, sans-serif; overflow: hidden; border-radius: 12px;';
      infoWindowContent.setAttribute('tabindex', '-1');
      infoWindowContent.setAttribute('contenteditable', 'false');

      const typeLabel = loc.type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());

      infoWindowContent.innerHTML = `
        <div style="overflow: hidden; cursor: pointer;">
          ${loc.imageUrl
            ? `<div style="position: relative; overflow: hidden;">
                <img src="${loc.imageUrl}" alt="${loc.name}" style="width:100%;height:110px;object-fit:cover;display:block;" />
              </div>`
            : `<div style="width:100%;height:80px;background:#f0f0f0;display:flex;align-items:center;justify-content:center;">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>`
          }
          <div style="padding: 10px 12px 12px;">
            <div style="font-weight: 600; font-size: 14px; color: #222; margin-bottom: 2px; line-height: 1.3; pointer-events: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${loc.name}</div>
            <div style="font-size: 12px; color: #717171; margin-bottom: 4px; pointer-events: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${loc.address}</div>
            <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 6px;">
              <span style="font-size: 11px; color: #E94057; font-weight: 500; pointer-events: none;">${typeLabel}</span>
              <span style="font-size: 12px; color: #222; font-weight: 600; text-decoration: underline; cursor: pointer;">Details</span>
            </div>
          </div>
        </div>
      `;

      const infoWindow = new google.maps.InfoWindow({
        content: infoWindowContent,
        disableAutoPan: false,
        maxWidth: 280,
      });

      marker.addListener('click', () => {
        infoWindow.open(mapInstanceRef.current!, marker);
      });

      // Add navigation on card click
      infoWindowContent.addEventListener('click', (e) => {
        e.stopPropagation();
        navigate(`/location/${loc.id}`);
      });
      
      markersRef.current.push(marker);
    });
    console.log('Markers added:', markersRef.current.length);
  }, [locations, navigate]);
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsLoggedIn(false);
    navigate('/login', { replace: true });
  };

  const handleNextCard = () => {
    setCarouselIndex((prev) => (prev + 1) % featuredSpots.length);
  };

  // Don't render anything until auth is checked
  if (!authChecked) {
    return null;
  }

  // Desktop Layout matching design
  const desktopLayoutContent = (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
      {/* Navigation Bar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: '#E94057',
          py: 0.5,
        }}
      >
        <Toolbar sx={{ maxWidth: 1400, width: '100%', mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BabyChangingStationIcon sx={{ color: '#fff', fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#fff',
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              BabyOnTheGo
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {['Home', 'Features', 'Resources', 'Contact'].map((item) => (
              <MuiLink
                key={item}
                href="#"
                sx={{
                  color: '#fff',
                  textDecoration: 'none',
                  fontWeight: 500,
                  '&:hover': { opacity: 0.8 },
                }}
              >
                {item}
              </MuiLink>
            ))}
            {isLoggedIn ? (
              <Button
                variant="outlined"
                onClick={handleLogout}
                size="small"
                sx={{
                  color: '#fff',
                  borderColor: '#fff',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Logout
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
                size="small"
                sx={{
                  color: '#fff',
                  borderColor: '#fff',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Search Bar */}
      <Box sx={{ bgcolor: '#fff', py: 1.5, px: 3, borderBottom: '1px solid #eee' }}>
        <Container maxWidth="lg">
          <TextField
            fullWidth
            placeholder="Search for locations, products, etc."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#f5f5f5',
                '& fieldset': { borderColor: '#e0e0e0' },
              },
            }}
          />
        </Container>
      </Box>

      {/* Main Content - Two Column Layout */}
      <Container maxWidth="lg" sx={{ py: 3, flex: 1 }}>
        <Grid container spacing={3}>
          {/* Left Column - Map and Categories */}
          <Grid size={{ xs: 12, md: 6 }}>
            {/* Interactive Google Map */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                mb: 2,
                '& *': {
                  caretColor: 'transparent !important',
                  userSelect: 'none !important',
                },
                '& .gm-style-iw-chr': {
                  display: 'none !important',
                },
                '& .gm-style-iw-c, & .gm-style-iw-d, & .gm-style-iw, & .gm-style-iw-tc': {
                  cursor: 'default !important',
                  caretColor: 'transparent !important',
                },
                '& .gm-style-iw-d': {
                  overflow: 'hidden !important',
                },
                '& [contenteditable="true"]': {
                  caretColor: 'transparent !important',
                  cursor: 'default !important',
                },
              }}
            >
              <Box
                ref={mapRef}
                sx={{
                  width: '100%',
                  height: '300px',
                  minHeight: '300px',
                  bgcolor: '#f5f5f5',
                  borderRadius: 3,
                  caretColor: 'transparent',
                  userSelect: 'none',
                  '& *': {
                    caretColor: 'transparent !important',
                  },
                }}
              />
            </Paper>

            {/* Category Chips */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  onClick={() => setSelectedCategory(category === selectedCategory ? null : category)}
                  sx={{
                    bgcolor: '#E94057',
                    color: '#fff',
                    fontWeight: 500,
                    '&:hover': {
                      bgcolor: '#d63649',
                    },
                  }}
                />
              ))}
            </Box>

            {/* Surprise Feature Section */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: '#fff',
                border: '1px solid #e0e0e0',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Surprise Feature
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                Introducing our new BabyOnTheGo app feature: "Baby's Day Out"! Plan your baby's
                day with ease, from diaper changes to feeding spots, all in one app. Get{' '}
                <MuiLink href="#" sx={{ color: '#E94057', textDecoration: 'underline' }}>
                  personalized recommendations
                </MuiLink>{' '}
                based on your location!
              </Typography>
            </Paper>
          </Grid>

          {/* Right Column - Featured Cards and Form */}
          <Grid size={{ xs: 12, md: 6 }}>
            {/* Featured Cards Row */}
            <Box sx={{ position: 'relative', mb: 3 }}>
              <Grid container spacing={2}>
                {featuredSpots.map((spot) => (
                  <Grid size={{ xs: 4 }} key={spot.id}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        height: '100%',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="80"
                        image={spot.image}
                        alt={spot.title}
                        sx={{ objectFit: 'cover' }}
                      />
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                          {spot.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.3, display: 'block' }}>
                          {spot.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <IconButton
                onClick={handleNextCard}
                size="small"
                sx={{
                  position: 'absolute',
                  right: -12,
                  top: '30%',
                  transform: 'translateY(-50%)',
                  bgcolor: '#E94057',
                  color: '#fff',
                  width: 24,
                  height: 24,
                  '&:hover': { bgcolor: '#d63649' },
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Post Your Location Section */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: '#fff',
                border: '1px solid #e0e0e0',
                mb: 2,
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                📍 Know a baby-friendly spot?
              </Typography>
              <PlaceAutocompleteTextField
                key="address-autocomplete"
                onPlaceSelect={handlePlaceSelect}
                placeholder="Search for an address…"
              />
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#aaa', textAlign: 'center' }}>
                Select a place and we'll take you to the posting form.
              </Typography>
            </Paper>

            {/* Available Nearby Stations */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                bgcolor: '#E94057',
                color: '#fff',
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Available Nearby Stations
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {locations.length > 0 ? (
                  locations.map((loc) => (
                    <Box
                      key={loc.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        bgcolor: 'rgba(255,255,255,0.15)',
                        borderRadius: 2,
                        p: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                      }}
                      onClick={() => navigate(`/location/${loc.id}`)}
                    >
                      <Box
                        component="img"
                        src={loc.imageUrl || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=80&h=80&fit=crop'}
                        alt={loc.name}
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 1.5,
                          objectFit: 'cover',
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {loc.name}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {loc.type.replace('_', ' ')}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        sx={{ ml: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/location/edit/${loc.id}`);
                        }}
                      >
                        Edit
                      </Button>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    No stations found. Be the first to add one!
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: '#2d3436',
          color: '#fff',
          py: 2,
          mt: 'auto',
        }}
      >
        <Container maxWidth="lg">
          <Grid container alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BabyChangingStationIcon sx={{ color: '#fff', fontSize: 24 }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  BabyOnTheGo
                </Typography>
              </Box>
              <Typography variant="caption" color="grey.400" display="block" mt={0.5}>
                Helping parents manage baby-related tasks with ease.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                <MuiLink href="#" sx={{ color: 'grey.400', fontSize: '0.75rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}>
                  Privacy Policy
                </MuiLink>
                <MuiLink href="#" sx={{ color: 'grey.400', fontSize: '0.75rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}>
                  Terms of Service
                </MuiLink>
                <MuiLink href="#" sx={{ color: 'grey.400', fontSize: '0.75rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}>
                  Contact Us
                </MuiLink>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: 'right' }}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <IconButton size="small" sx={{ color: '#fff' }}>
                  <FacebookIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ color: '#fff' }}>
                  <TwitterIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ color: '#fff' }}>
                  <InstagramIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );

  // Mobile Layout (keeping original mobile design)
  const mobileLayoutContent = (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>
      {/* App Bar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{ bgcolor: '#fff', borderBottom: '1px solid #E0E0E0' }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            aria-label="menu"
            data-testid="menu-button"
            sx={{ mr: 2, color: '#333' }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            component="div"
            data-testid="app-title"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              color: '#FF6B6B',
              fontFamily: '"Poppins", sans-serif',
            }}
          >
            BabyOnTheGo
          </Typography>
          <IconButton
            aria-label="notifications"
            data-testid="notifications-button"
            sx={{ color: '#333' }}
          >
            <NotificationsIcon />
          </IconButton>
          <Avatar
            data-testid="user-avatar"
            sx={{ ml: 1, width: 32, height: 32, bgcolor: '#FF6B6B' }}
          >
            U
          </Avatar>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="sm" sx={{ py: 3 }}>
        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search for locations, products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#999' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              bgcolor: '#fff',
            },
          }}
        />

        {/* Featured Spots Carousel */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Featured Spots
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid #e0e0e0',
              }}
            >
              <CardMedia
                component="img"
                height="160"
                image={featuredSpots[carouselIndex].image}
                alt={featuredSpots[carouselIndex].title}
              />
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600}>
                  {featuredSpots[carouselIndex].title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {featuredSpots[carouselIndex].description}
                </Typography>
              </CardContent>
            </Card>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
              {featuredSpots.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCarouselIndex(index)}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: index === carouselIndex ? '#E94057' : '#ddd',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Category Chips */}
        <Box sx={{ mb: 3, overflowX: 'auto', display: 'flex', gap: 1, pb: 1 }}>
          {categories.map((category) => (
            <Chip
              key={category}
              label={category}
              onClick={() => setSelectedCategory(category === selectedCategory ? null : category)}
              sx={{
                bgcolor: selectedCategory === category ? '#E94057' : '#f0f0f0',
                color: selectedCategory === category ? '#fff' : '#333',
                fontWeight: 500,
                flexShrink: 0,
              }}
            />
          ))}
        </Box>

        {/* Post a Location */}
        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: 3, bgcolor: '#fff', border: '1px solid #e0e0e0', mb: 3 }}
        >
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            📍 Know a baby-friendly spot?
          </Typography>
          <PlaceAutocompleteTextField
            key="mobile-address-autocomplete"
            onPlaceSelect={handlePlaceSelect}
            placeholder="Search for an address…"
          />
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#aaa', textAlign: 'center' }}>
            Select a place and we'll take you there.
          </Typography>
        </Paper>

        {/* Nearby Stations */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            bgcolor: '#E94057',
            color: '#fff',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Nearby Stations
          </Typography>
          {locations.length > 0 ? (
            locations.map((loc) => (
              <Typography key={loc.id} variant="body2" sx={{ mb: 0.5 }}>
                {loc.name} - {loc.type}
              </Typography>
            ))
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              No stations found. Be the first to add one!
            </Typography>
          )}
        </Paper>
      </Container>
    </Box>
  );

  // Return the appropriate layout - use the JSX directly, not as component calls
  return isMobile ? mobileLayoutContent : desktopLayoutContent;
};

export default Home;
